import React, { useState, useEffect } from 'react';
import { SPM5_LATITUDE_DATA, SPM_FIGURE_METADATA } from '../../data/ipccData';
import { AudioEngine } from '../../audio/AudioEngine';
import { FigureDescriptionPanel } from '../FigureDescriptionPanel';
import { Compass, Play, Pause, Thermometer, ShieldAlert } from 'lucide-react';

interface SPM5SpatialProps {
  isPlaying: boolean;
  onTogglePlay: () => void;
}

type WarmingLevel = '1.5' | '2.0' | '4.0';

export const SPM5Spatial: React.FC<SPM5SpatialProps> = ({ isPlaying, onTogglePlay }) => {
  const meta = SPM_FIGURE_METADATA['spm5'];
  const audioEngine = AudioEngine.getInstance();

  const [selectedLevel, setSelectedLevel] = useState<WarmingLevel>('2.0');
  const [scanIndex, setScanIndex] = useState<number>(0);

  // South-to-North latitude scan loop
  useEffect(() => {
    let timer: number;
    if (isPlaying) {
      timer = window.setInterval(() => {
        setScanIndex((prev) => {
          const next = (prev + 1) % SPM5_LATITUDE_DATA.length;
          triggerLatitudeAudio(next);
          return next;
        });
      }, 600);
    } else {
      audioEngine.stopNatWarmth();
      audioEngine.stopAnthropoPulse();
    }
    return () => clearInterval(timer);
  }, [isPlaying, selectedLevel]);

  const triggerLatitudeAudio = (idx: number) => {
    if (!audioEngine.getIsUnlocked()) return;
    const item = SPM5_LATITUDE_DATA[idx];
    if (!item) return;

    let temp = item.temp2_0;
    if (selectedLevel === '1.5') temp = item.temp1_5;
    if (selectedLevel === '4.0') temp = item.temp4_0;

    const freq = audioEngine.tempToFreq(temp);
    // Polar amplification produces intense FM modulation index
    const fmIndex = item.latitude > 50 ? 8 : 3;

    audioEngine.startAnthropoPulse(freq, fmIndex, 0.4);
  };

  const handleSelectLatitude = (idx: number) => {
    setScanIndex(idx);
    triggerLatitudeAudio(idx);
  };

  const activeLat = SPM5_LATITUDE_DATA[scanIndex];

  return (
    <div className="space-y-6">
      {/* Title Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 glass-panel p-5 rounded-2xl border border-slate-800">
        <div>
          <span className="text-xs font-mono text-cyan-400 font-semibold">{meta.number}</span>
          <h2 className="text-xl font-extrabold text-white">{meta.title}</h2>
          <p className="text-xs text-slate-400 mt-1">{meta.subtitle}</p>
        </div>

        {/* Global Warming Level Buttons */}
        <div className="flex items-center flex-wrap gap-3">
          <div className="flex items-center gap-1 bg-slate-900 p-1 rounded-xl border border-slate-800 text-xs">
            <button
              onClick={() => setSelectedLevel('1.5')}
              className={`px-3 py-1.5 rounded-lg font-mono font-bold transition-all ${
                selectedLevel === '1.5' ? 'bg-cyan-500 text-slate-950 glow-cyan' : 'text-slate-400 hover:text-white'
              }`}
            >
              +1.5°C GWL
            </button>
            <button
              onClick={() => setSelectedLevel('2.0')}
              className={`px-3 py-1.5 rounded-lg font-mono font-bold transition-all ${
                selectedLevel === '2.0' ? 'bg-amber-500 text-slate-950 glow-amber' : 'text-slate-400 hover:text-white'
              }`}
            >
              +2.0°C GWL
            </button>
            <button
              onClick={() => setSelectedLevel('4.0')}
              className={`px-3 py-1.5 rounded-lg font-mono font-bold transition-all ${
                selectedLevel === '4.0' ? 'bg-red-500 text-slate-950 glow-red' : 'text-slate-400 hover:text-white'
              }`}
            >
              +4.0°C GWL
            </button>
          </div>

          <button
            onClick={onTogglePlay}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all shadow-lg ${
              isPlaying ? 'bg-amber-500 text-slate-950 glow-amber' : 'bg-cyan-500 text-slate-950 glow-cyan'
            }`}
          >
            {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            <span>{isPlaying ? 'Pause Scan' : 'South-to-North S->N Scan'}</span>
          </button>
        </div>
      </div>

      {/* Main Latitude Scan Module */}
      <div className="glass-panel p-6 rounded-3xl border border-slate-800 space-y-6">
        <div className="flex items-center justify-between text-xs text-slate-400 font-mono">
          <span className="flex items-center gap-2">
            <Compass className="w-4 h-4 text-cyan-400" />
            <span>Latitude Band Scanning (-80°S to +80°N Arctic)</span>
          </span>
          <span className="text-cyan-300 font-bold">
            Active Band: {activeLat.name}
          </span>
        </div>

        {/* Latitude Bars Grid */}
        <div className="space-y-2.5">
          {SPM5_LATITUDE_DATA.map((item, idx) => {
            const isScanning = isPlaying && scanIndex === idx;
            const isSelected = scanIndex === idx;

            let temp = item.temp2_0;
            if (selectedLevel === '1.5') temp = item.temp1_5;
            if (selectedLevel === '4.0') temp = item.temp4_0;

            const isHighArctic = item.latitude >= 60;
            const barPercent = Math.min(100, (temp / 10.0) * 100);

            return (
              <div
                key={item.latitude}
                onClick={() => handleSelectLatitude(idx)}
                className={`p-3 rounded-2xl border cursor-pointer transition-all duration-200 glass-panel-hover flex items-center justify-between gap-4 ${
                  isScanning || isSelected
                    ? 'bg-slate-800 border-cyan-400 glow-cyan shadow-md'
                    : 'bg-slate-900/60 border-slate-800 hover:border-slate-700'
                }`}
              >
                <div className="w-44 text-xs font-mono font-bold text-slate-200 flex items-center gap-2">
                  <span>{item.name}</span>
                  {isHighArctic && (
                    <span className="px-1.5 py-0.5 rounded text-[9px] bg-red-950 text-red-400 border border-red-800 font-sans">
                      Polar Amp
                    </span>
                  )}
                </div>

                <div className="flex-1 bg-slate-950 h-5 rounded-lg overflow-hidden border border-slate-800 relative">
                  <div
                    className={`h-full transition-all duration-500 rounded-lg ${
                      isHighArctic
                        ? 'bg-gradient-to-r from-orange-500 to-red-600'
                        : 'bg-gradient-to-r from-cyan-500 to-blue-600'
                    }`}
                    style={{ width: `${barPercent}%` }}
                  ></div>
                </div>

                <div className="w-24 text-right font-mono text-xs font-bold text-white">
                  +{temp.toFixed(1)}°C
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Scientific Synopsis & Original Figure Reference Panel */}
      <FigureDescriptionPanel meta={meta} />
    </div>
  );
};
